import { rgba } from 'polished';
import { pxToRem } from 'modules/spacing';
import { setGradient } from './utils';
const brand = {
    '50': '#FFF5BA',
    '500': '#FFDE1C',
};
const gray = {
    '1000': '#FFFFFF',
    '900': '#F8F8F9',
    '800': '#F3F3F5',
    '700': '#DBDBDF',
    '600': '#CACAD0',
    '500': '#B1B1BB',
    '400': '#A2A2AD',
    '300': '#8B8B99',
    '200': '#7E7E8B',
    '100': '#63636D',
    '50': '#4C4C54',
    '25': '#3A3A40',
    '0': '#161618',
};
const blue = {
    '900': '#E9F1FE',
    '800': '#BCD2FB',
    '700': '#9CBCF9',
    '600': '#6E9EF6',
    '500': '#528BF4',
    '400': '#276EF1',
    '300': '#2364DB',
    '200': '#1C4EAB',
    '100': '#153D85',
    '50': '#102E65',
};
const indigo = {
    '900': '#EEEEFA',
    '800': '#CAC9EF',
    '700': '#B1AFE8',
    '600': '#8D8BDD',
    '500': '#7775D6',
    '400': '#5552CC',
    '300': '#4D4BBA',
    '200': '#3C3A91',
    '100': '#2F2D70',
    '50': '#242256',
};
const purple = {
    '900': '#F3F0F9',
    '800': '#D8D0ED',
    '700': '#C6B9E5',
    '600': '#AB98D9',
    '500': '#9B85D1',
    '400': '#8266C6',
    '300': '#765DB4',
    '200': '#5C488D',
    '100': '#48386D',
    '50': '#372B53',
};
const magenta = {
    '900': '#F8EDF5',
    '800': '#EBC8DF',
    '700': '#E1ADCF',
    '600': '#D387B9',
    '500': '#CA70AC',
    '400': '#BD4C97',
    '300': '#AC4589',
    '200': '#86366B',
    '100': '#682A53',
    '50': '#4F203F',
};
const red = {
    '900': '#FBECEA',
    '800': '#F3C3BE',
    '700': '#EDA69E',
    '600': '#E57D72',
    '500': '#E06456',
    '400': '#D83D2C',
    '300': '#C53828',
    '200': '#992B1F',
    '100': '#772218',
    '50': '#5B1A12',
};
const orange = {
    '900': '#FEF2ED',
    '800': '#FAD7C6',
    '700': '#F8C3AA',
    '600': '#F5A884',
    '500': '#F3976C',
    '400': '#F07D47',
    '300': '#DA7241',
    '200': '#AA5932',
    '100': '#844527',
    '50': '#65351E',
};
const brown = {
    '900': '#F7F4F1',
    '800': '#EBE5DE',
    '700': '#E0D6CA',
    '600': '#D3C5B4',
    '500': '#C3B19A',
    '400': '#B29A7D',
    '300': '#A38664',
    '200': '#826A4D',
    '100': '#6F5A42',
    '50': '#554532',
};
const yellow = {
    '900': '#FFF9EC',
    '800': '#FFEBC5',
    '700': '#FFE2A9',
    '600': '#FFD581',
    '500': '#FFCD69',
    '400': '#FFC043',
    '300': '#E8AF3D',
    '200': '#B58830',
    '100': '#8C6A25',
    '50': '#6B511C',
};
const green = {
    '900': '#EBF6F0',
    '800': '#C2E2D1',
    '700': '#A4D5BB',
    '600': '#7AC19C',
    '500': '#61B589',
    '400': '#39A36B',
    '300': '#349461',
    '200': '#28744C',
    '100': '#1F5A3B',
    '50': '#18442D',
};
const teal = {
    '900': '#E7F3F2',
    '800': '#B3D9D5',
    '700': '#8EC7C1',
    '600': '#5BADA5',
    '500': '#3B9D94',
    '400': '#0A8579',
    '300': '#09796E',
    '200': '#075E56',
    '100': '#064943',
    '50': '#043833',
};
const cyan = {
    '900': '#E9F2F4',
    '800': '#B9D8DE',
    '700': '#98C5CE',
    '600': '#68ABB7',
    '500': '#4B9AA9',
    '400': '#1E8194',
    '300': '#1B7587',
    '200': '#155C69',
    '100': '#114751',
    '50': '#0D363E',
};
const brandPrimary = {
    '0': gray['0'],
    '25': gray['25'],
    '50': gray['50'],
    '100': gray['100'],
    '200': gray['200'],
    '300': gray['300'],
    '400': gray['400'],
    '500': gray['500'],
    '600': gray['600'],
    '700': gray['700'],
    '800': gray['800'],
    '900': gray['900'],
    '1000': gray['1000'],
};
const primary = {
    '0': gray['0'],
    '25': gray['25'],
    '50': gray['50'],
    '100': gray['100'],
    '200': gray['200'],
    '300': gray['300'],
    '400': gray['400'],
    '500': gray['500'],
    '600': gray['600'],
    '700': gray['700'],
    '800': gray['800'],
    '900': gray['900'],
    '1000': gray['1000'],
};
const secondary = {
    '50': blue['50'],
    '100': blue['100'],
    '200': blue['200'],
    '300': blue['300'],
    '400': blue['400'],
    '500': blue['500'],
    '600': blue['600'],
    '700': blue['700'],
    '800': blue['800'],
    '900': blue['900'],
};
const ai = {
    '50': setGradient(blue['50'], indigo['50']),
    '100': setGradient(blue['100'], indigo['100']),
    '200': setGradient(blue['200'], indigo['200']),
    '300': setGradient(blue['300'], indigo['300']),
    '400': setGradient(blue['400'], indigo['400']),
    '500': setGradient(blue['500'], indigo['500']),
    '600': setGradient(blue['600'], indigo['600']),
    '700': setGradient(blue['700'], indigo['700']),
    '800': setGradient(blue['800'], indigo['800']),
    '900': setGradient(blue['900'], indigo['900']),
};
const background = {
    '50': gray['0'],
    '75': gray['25'],
    '100': gray['50'],
};
const text = {
    '100': gray['700'],
    '200': gray['800'],
    '300': gray['1000'],
    light: gray['0'],
    success: green['500'],
    danger: red['500'],
    ai: '', // defined in /modules/tetrisTheme/utils.ts
};
const border = {
    '100': gray['100'],
    '200': gray['500'],
    '300': gray['700'],
};
const disabled = {
    '100': gray['100'],
    '200': gray['500'],
    '300': gray['700'],
};
const assistant = {
    '50': purple['50'],
    '100': purple['100'],
    '200': purple['200'],
    '300': purple['300'],
    '400': purple['400'],
    '500': purple['500'],
    '600': purple['600'],
    '700': purple['700'],
    '800': purple['800'],
    '900': purple['900'],
};
const focus = {
    primary: primary['200'],
    secondary: secondary['100'],
    success: green['200'],
    danger: red['200'],
    warning: yellow['200'],
    info: blue['200'],
    assistant: assistant['100'],
    ai: ai['200'],
};
export const darkSpectrum = {
    gray,
    blue,
    indigo,
    purple,
    magenta,
    red,
    orange,
    brown,
    yellow,
    green,
    teal,
    cyan,
};
export const darkThemeColors = {
    brand,
    primary,
    brandPrimary,
    secondary,
    assistant,
    background,
    text,
    border,
    disabled,
    focus,
    ai,
};
const shadowToken = darkSpectrum.gray[0];
const darkDropShadowTokens = {
    '100': `0 ${pxToRem(2)} ${pxToRem(2)} ${rgba(shadowToken, 0.04)}, 0 0 ${pxToRem(2)} ${rgba(shadowToken, 0.04)}`,
    '200': `0 ${pxToRem(1)} ${pxToRem(2)} ${rgba(shadowToken, 0.3)}, 0 ${pxToRem(2)} ${pxToRem(6)} ${rgba(shadowToken, 0.15)}`,
    '300': `0 ${pxToRem(8)} ${pxToRem(20)} ${rgba(shadowToken, 0.08)}, 0 ${pxToRem(4)} ${pxToRem(18)} ${rgba(shadowToken, 0.04)}`,
    '400': `0 ${pxToRem(6)} ${pxToRem(18)} ${rgba(shadowToken, 0.12)}, 0 ${pxToRem(14)} ${pxToRem(20)} ${rgba(shadowToken, 0.16)}`,
};
export const darkColorTokens = Object.assign(Object.assign(Object.assign({}, darkSpectrum), darkThemeColors), { darkDropShadowTokens });
